import React, { useEffect, useRef, useState } from 'react';
import ctaCteApiService from '../services/CtaCteApiService';
import TerminosYCondiciones from './common/TerminosYCondiciones';
import { getTerminosYCondicionesTexto } from '../constants/terminosYCondiciones';

const EMPTY_FORM = {
  nombre: '',
  apellido: '',
  email: '',
  password: '',
  repeatPassword: '',
  dni: '',
  telefono: ''
};

const formatCurrency = (value, moneda = 'ARS') => new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: moneda || 'ARS',
  maximumFractionDigits: 2
}).format(Number(value || 0));

const RegisterModal = ({ onClose, onRegistered }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [aceptoTerminos, setAceptoTerminos] = useState(false);
  const [costo, setCosto] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const captchaRef = useRef(null);
  const captchaWidgetId = useRef(null);
  const [captchaToken, setCaptchaToken] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await ctaCteApiService.getCostoSuscripcion();
        if (!cancelled) setCosto(data);
      } catch {
        // El precio es informativo; si falla no bloqueamos el registro.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (window.turnstile || document.getElementById('turnstile-script')) return;

    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (captchaWidgetId.current) return undefined;

    const timer = setInterval(() => {
      if (window.turnstile && captchaRef.current && !captchaWidgetId.current) {
        clearInterval(timer);
        try {
          captchaWidgetId.current = window.turnstile.render(captchaRef.current, {
            sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
            callback: (token) => {
              setCaptchaToken(token);
            },
            'error-callback': () => {
              setCaptchaToken('');
            },
            'expired-callback': () => {
              setCaptchaToken('');
            }
          });
        } catch (err) {
          console.error('No se pudo inicializar el captcha de Turnstile:', err);
          setError('No se pudo cargar el captcha. Verificá la configuración de Turnstile.');
        }
      }
    }, 200);

    return () => clearInterval(timer);
  }, []);

  const resetCaptcha = () => {
    setCaptchaToken('');
    if (window.turnstile && captchaWidgetId.current) {
      window.turnstile.reset(captchaWidgetId.current);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.nombre.trim() || !form.apellido.trim() || !form.email.trim() || !form.password || !form.dni.trim()) {
      setError('Completá todos los campos obligatorios.');
      return;
    }

    if (form.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (form.password !== form.repeatPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    if (!aceptoTerminos) {
      setError('Tenés que aceptar los términos y condiciones para registrarte.');
      return;
    }

    if (!captchaToken) {
      setError('Completá el captcha para continuar.');
      return;
    }

    setLoading(true);
    try {
      await ctaCteApiService.register({
        nombre: form.nombre.trim(),
        apellido: form.apellido.trim(),
        email: form.email.trim(),
        password: form.password,
        repeatPassword: form.repeatPassword,
        dni: form.dni.trim(),
        telefono: form.telefono.trim(),
        aceptoTerminos,
        terminosYCondicionesTexto: getTerminosYCondicionesTexto(),
        captchaToken
      });

      setSuccess('¡Cuenta creada correctamente! Ya podés iniciar sesión.');
      setForm(EMPTY_FORM);
      setAceptoTerminos(false);
      resetCaptcha();

      if (typeof onRegistered === 'function') {
        onRegistered();
      }
    } catch (err) {
      setError(err.message || 'No pudimos crear la cuenta. Intentá nuevamente.');
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-modal-overlay" onClick={onClose}>
      <div className="reset-modal-card register-modal-card" onClick={(event) => event.stopPropagation()}>
        <h3>Crear cuenta</h3>
        <p>Completá tus datos para empezar a usar CtaCte App.</p>

        {costo && (
          <div className="register-price-banner">
            <i className="fas fa-tag"></i>
            <span>
              Período de prueba gratuito de <strong>{costo.trialDays ?? 15} días corridos</strong>. Luego, la
              suscripción mensual es de <strong>{formatCurrency(costo.monto, costo.moneda)}</strong>.
            </span>
          </div>
        )}

        {error && <div className="reset-modal-error">{error}</div>}
        {success && <div className="reset-modal-success">{success}</div>}

        {!success && (
          <form onSubmit={handleSubmit} className="register-form">
            <div className="register-form-grid">
              <div>
                <label className="reset-modal-label" htmlFor="reg-nombre">Nombre</label>
                <input
                  id="reg-nombre"
                  className="reset-modal-input"
                  value={form.nombre}
                  onChange={(e) => handleChange('nombre', e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
              <div>
                <label className="reset-modal-label" htmlFor="reg-apellido">Apellido</label>
                <input
                  id="reg-apellido"
                  className="reset-modal-input"
                  value={form.apellido}
                  onChange={(e) => handleChange('apellido', e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <label className="reset-modal-label" htmlFor="reg-email">Email</label>
            <input
              id="reg-email"
              type="email"
              className="reset-modal-input"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              disabled={loading}
              required
            />

            <div className="register-form-grid">
              <div>
                <label className="reset-modal-label" htmlFor="reg-password">Contraseña</label>
                <input
                  id="reg-password"
                  type="password"
                  className="reset-modal-input"
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Al menos 6 caracteres"
                  disabled={loading}
                  required
                />
              </div>
              <div>
                <label className="reset-modal-label" htmlFor="reg-repeat-password">Repetir contraseña</label>
                <input
                  id="reg-repeat-password"
                  type="password"
                  className="reset-modal-input"
                  value={form.repeatPassword}
                  onChange={(e) => handleChange('repeatPassword', e.target.value)}
                  placeholder="Repetí tu contraseña"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className="register-form-grid">
              <div>
                <label className="reset-modal-label" htmlFor="reg-dni">DNI</label>
                <input
                  id="reg-dni"
                  className="reset-modal-input"
                  value={form.dni}
                  onChange={(e) => handleChange('dni', e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
              <div>
                <label className="reset-modal-label" htmlFor="reg-telefono">Teléfono (opcional)</label>
                <input
                  id="reg-telefono"
                  className="reset-modal-input"
                  value={form.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="terminos-box">
              <TerminosYCondiciones />
            </div>

            <label className="checkbox-wrapper">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={aceptoTerminos}
                onChange={(e) => setAceptoTerminos(e.target.checked)}
                disabled={loading}
              />
              <span className="checkbox-custom"></span>
              Leí y acepto los términos y condiciones
            </label>

            <div ref={captchaRef} className="turnstile-slot"></div>

            <div className="reset-modal-actions">
              <button type="button" className="register-secondary-btn" onClick={onClose} disabled={loading}>
                Cancelar
              </button>
              <button type="submit" className="login-button" disabled={loading || !aceptoTerminos}>
                {loading ? 'Creando cuenta...' : 'Crear cuenta'}
              </button>
            </div>
          </form>
        )}

        {success && (
          <div className="reset-modal-actions">
            <button type="button" className="login-button" onClick={onClose}>
              Volver al login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterModal;
